// ══════════════════════════════════════════════════════════════
// District88 — Shared localStorage hooks for 100 features
// Recently viewed, saved posts, drafts, wishlists, preferences
// ══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { trackCultureMission } from '../lib/cultureMissionTracker';
import { collectorCultureService } from '../services/collectorCultureService';

function usePersistedState<T>(key: string, defaultValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : defaultValue; } catch { return defaultValue; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(value)); } catch {} }, [key, value]);
  return [value, setValue];
}

// ── 1. Recently Viewed Items ─────────────────────────────────
export interface RecentItem { id: string; type: 'listing' | 'profile' | 'post'; title: string; image?: string; price?: number; viewedAt: string; }

export function useRecentlyViewed() {
  const [items, setItems] = usePersistedState<RecentItem[]>('d88_recent', []);
  const add = useCallback((item: Omit<RecentItem, 'viewedAt'>) => {
    setItems(prev => [{ ...item, viewedAt: new Date().toISOString() }, ...prev.filter(i => i.id !== item.id)].slice(0, 30));
  }, [setItems]);
  return { items, add };
}

// ── 2. Saved Posts / Bookmarks ───────────────────────────────
export function useSavedPosts() {
  const [saved, setSaved] = usePersistedState<string[]>('d88_saved_posts', []);
  const toggle = useCallback((id: string) => {
    setSaved(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, [setSaved]);
  const isSaved = useCallback((id: string) => saved.includes(id), [saved]);
  return { saved, toggle, isSaved };
}

// ── 3. Saved Collections ─────────────────────────────────────
export interface Collection { id: string; name: string; postIds: string[]; createdAt: string; }

export function useCollections() {
  const [collections, setCollections] = usePersistedState<Collection[]>('d88_collections', []);
  const create = useCallback((name: string) => {
    const c: Collection = { id: Date.now().toString(), name, postIds: [], createdAt: new Date().toISOString() };
    setCollections(prev => [...prev, c]);
    return c;
  }, [setCollections]);
  const addToCollection = useCallback((collectionId: string, postId: string) => {
    setCollections(prev => prev.map(c => c.id === collectionId ? { ...c, postIds: Array.from(new Set([...c.postIds, postId])) } : c));
  }, [setCollections]);
  const remove = useCallback((id: string) => setCollections(prev => prev.filter(c => c.id !== id)), [setCollections]);
  return { collections, create, addToCollection, remove };
}

// ── 4. Post Drafts ───────────────────────────────────────────
export interface Draft { id: string; content: string; images: string[]; tags: string[]; updatedAt: string; }

export function useDrafts() {
  const [drafts, setDrafts] = usePersistedState<Draft[]>('d88_drafts', []);
  const save = useCallback((draft: Omit<Draft, 'id' | 'updatedAt'>) => {
    const d: Draft = { ...draft, id: Date.now().toString(), updatedAt: new Date().toISOString() };
    setDrafts(prev => [d, ...prev].slice(0, 20));
    return d;
  }, [setDrafts]);
  const update = useCallback((id: string, changes: Partial<Draft>) => {
    setDrafts(prev => prev.map(d => d.id === id ? { ...d, ...changes, updatedAt: new Date().toISOString() } : d));
  }, [setDrafts]);
  const deleteDraft = useCallback((id: string) => setDrafts(prev => prev.filter(d => d.id !== id)), [setDrafts]);
  return { drafts, save, update, deleteDraft };
}

// ── 5. Saved Searches ────────────────────────────────────────
export function useSavedSearches() {
  const [searches, setSearches] = usePersistedState<{ query: string; savedAt: string }[]>('d88_saved_searches', []);
  const save = useCallback((query: string) => {
    setSearches(prev => [{ query, savedAt: new Date().toISOString() }, ...prev.filter(s => s.query !== query)].slice(0, 20));
  }, [setSearches]);
  const remove = useCallback((query: string) => setSearches(prev => prev.filter(s => s.query !== query)), [setSearches]);
  return { searches, save, remove };
}

// ── 6. Wishlist ──────────────────────────────────────────────
export function useWishlist() {
  const { user } = useAuth();
  const [wishlist, setWishlist] = usePersistedState<string[]>('d88_wishlist', []);
  const trackSave = useCallback(
    (id: string) => {
      if (user?.id && /^[0-9a-f-]{36}$/i.test(id)) {
        void collectorCultureService.trackTrendSave(user.id, id);
        void trackCultureMission('save_item');
      }
    },
    [user?.id],
  );
  const toggle = useCallback((id: string) => {
    setWishlist((prev) => {
      const adding = !prev.includes(id);
      if (adding) trackSave(id);
      return adding ? [...prev, id] : prev.filter((x) => x !== id);
    });
  }, [setWishlist, trackSave]);
  const add = useCallback((id: string) => {
    setWishlist((prev) => {
      if (prev.includes(id)) return prev;
      trackSave(id);
      return [...prev, id];
    });
  }, [setWishlist, trackSave]);
  const isWished = useCallback((id: string) => wishlist.includes(id), [wishlist]);
  return { wishlist, toggle, add, isWished };
}

/** Saved clip post IDs → Vault / flex queue */
export function useVaultClipWishlist() {
  const [ids, setIds] = usePersistedState<string[]>('d88_vault_clip_wishlist', []);
  const addClip = useCallback((id: string) => {
    setIds((prev) => [id, ...prev.filter((x) => x !== id)].slice(0, 100));
  }, [setIds]);
  const removeClip = useCallback((id: string) => {
    setIds((prev) => prev.filter((x) => x !== id));
  }, [setIds]);
  return { clipIds: ids, addClip, removeClip };
}

// ── 7. Interest Tags / Onboarding ────────────────────────────
export const ALL_INTEREST_TAGS = [
  'streetwear', 'vintage', 'designer', 'resale', 'sneakers', 'y2k', 'archive',
  'minimal', 'hype', 'luxury', 'skatewear', 'workwear', 'techwear', 'athleisure',
  'denim', 'nike', 'jordan', 'adidas', 'new-balance', 'supreme', 'stussy'
];

export const ALL_VIBES = ['Y2K', 'Archive', 'Minimal', 'Hype', 'Vintage', 'Gorpcore', 'Dark', 'Prep', 'Skate', 'Luxury'];

export function useInterestTags() {
  const [tags, setTags] = usePersistedState<string[]>('d88_interests', []);
  const [onboarded, setOnboarded] = usePersistedState<boolean>('d88_onboarded', false);
  const toggle = useCallback((tag: string) => {
    setTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  }, [setTags]);
  const completeOnboarding = useCallback(() => setOnboarded(true), [setOnboarded]);
  return { tags, toggle, onboarded, completeOnboarding, setTags };
}

// ── 8. Followed Topics ───────────────────────────────────────
export function useFollowedTopics() {
  const [topics, setTopics] = usePersistedState<string[]>('d88_followed_topics', []);
  const toggle = useCallback((topic: string) => {
    setTopics(prev => prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]);
  }, [setTopics]);
  const isFollowing = useCallback((topic: string) => topics.includes(topic), [topics]);
  return { topics, toggle, isFollowing };
}

// ── 9. Notification Preferences ──────────────────────────────
export interface NotifPrefs {
  push: boolean; email: boolean; weeklyRecap: boolean; dropReminders: boolean;
  sellerAlerts: boolean; trendingAlerts: boolean; newFollowers: boolean;
  mentions: boolean; quietHoursStart: number; quietHoursEnd: number;
}

const defaultNotifPrefs: NotifPrefs = {
  push: true, email: true, weeklyRecap: true, dropReminders: true,
  sellerAlerts: true, trendingAlerts: true, newFollowers: true,
  mentions: true, quietHoursStart: 22, quietHoursEnd: 7,
};

export function useNotifPrefs() {
  const [prefs, setPrefs] = usePersistedState<NotifPrefs>('d88_notif_prefs', defaultNotifPrefs);
  const update = useCallback((changes: Partial<NotifPrefs>) => setPrefs(prev => ({ ...prev, ...changes })), [setPrefs]);
  return { prefs, update };
}

// ── 10. Privacy & Security ───────────────────────────────────
export interface PrivacySettings {
  profilePublic: boolean; showActivity: boolean; allowMessages: 'everyone' | 'followers' | 'none';
  showOnlineStatus: boolean; twoFactorEnabled: boolean;
}

export function usePrivacySettings() {
  const [settings, setSettings] = usePersistedState<PrivacySettings>('d88_privacy', {
    profilePublic: true, showActivity: true, allowMessages: 'everyone', showOnlineStatus: true, twoFactorEnabled: false
  });
  const update = useCallback((changes: Partial<PrivacySettings>) => setSettings(prev => ({ ...prev, ...changes })), [setSettings]);
  return { settings, update };
}

// ── 11. Blocked Users ────────────────────────────────────────
export function useBlockedUsers() {
  const [blocked, setBlocked] = usePersistedState<string[]>('d88_blocked', []);
  const block = useCallback((id: string) => setBlocked(prev => [...prev, id]), [setBlocked]);
  const unblock = useCallback((id: string) => setBlocked(prev => prev.filter(x => x !== id)), [setBlocked]);
  const isBlocked = useCallback((id: string) => blocked.includes(id), [blocked]);
  return { blocked, block, unblock, isBlocked };
}

// ── 12. Connection Notes ─────────────────────────
export function useConnectionNotes() {
  const [notes, setNotes] = usePersistedState<Record<string, string>>('d88_connection_notes', {});
  const setNote = useCallback((userId: string, note: string) => setNotes(prev => ({ ...prev, [userId]: note })), [setNotes]);
  const getNote = useCallback((userId: string) => notes[userId] || '', [notes]);
  return { notes, setNote, getNote };
}

// ── 13. Connection Streaks ───────────────────────────────────
export function useStreaks() {
  const [streaks, setStreaks] = usePersistedState<Record<string, { count: number; lastDate: string }>>('d88_streaks', {});
  const recordInteraction = useCallback((userId: string) => {
    const today = new Date().toDateString();
    setStreaks(prev => {
      const existing = prev[userId];
      if (existing?.lastDate === today) return prev;
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      const count = existing?.lastDate === yesterday ? existing.count + 1 : 1;
      return { ...prev, [userId]: { count, lastDate: today } };
    });
  }, [setStreaks]);
  const getStreak = useCallback((userId: string) => streaks[userId]?.count || 0, [streaks]);
  return { streaks, recordInteraction, getStreak };
}

// ── 14. Offer/Negotiation History ────────────────────────────
export interface OfferRecord { listingId: string; amount: number; status: 'pending' | 'accepted' | 'rejected' | 'countered'; date: string; counterAmount?: number; }

export function useOfferHistory() {
  const [offers, setOffers] = usePersistedState<OfferRecord[]>('d88_offers', []);
  const addOffer = useCallback((offer: Omit<OfferRecord, 'date'>) => {
    setOffers(prev => [{ ...offer, date: new Date().toISOString() }, ...prev]);
  }, [setOffers]);
  const getForListing = useCallback((listingId: string) => offers.filter(o => o.listingId === listingId), [offers]);
  return { offers, addOffer, getForListing };
}

// ── 15. Auto-accept Offer Rules ──────────────────────────────
export interface AutoAcceptRule { minPrice: number; condition?: string; enabled: boolean; }

export function useAutoAcceptRules() {
  const [rules, setRules] = usePersistedState<Record<string, AutoAcceptRule>>('d88_auto_accept', {});
  const setRule = useCallback((listingId: string, rule: AutoAcceptRule) => setRules(prev => ({ ...prev, [listingId]: rule })), [setRules]);
  return { rules, setRule };
}

// ── 16. Sales Goals ──────────────────────────────────────────
export function useSalesGoals() {
  const [goals, setGoals] = usePersistedState<{ monthly: number; achieved: number }>('d88_sales_goals', { monthly: 0, achieved: 0 });
  const setGoal = useCallback((monthly: number) => setGoals(prev => ({ ...prev, monthly })), [setGoals]);
  const recordSale = useCallback((amount: number) => setGoals(prev => ({ ...prev, achieved: prev.achieved + amount })), [setGoals]);
  return { goals, setGoal, recordSale };
}

// ── 17. Learning Streaks & Badges ────────────────────────────
export function useLearningProgress() {
  const [progress, setProgress] = usePersistedState<{
    streak: number; lastLearnDate: string; badges: string[]; completedCourses: string[];
    totalXP: number;
  }>('d88_learning', { streak: 0, lastLearnDate: '', badges: [], completedCourses: [], totalXP: 0 });

  const recordLesson = useCallback((courseId?: string) => {
    const today = new Date().toDateString();
    setProgress(prev => {
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      const streak = prev.lastLearnDate === yesterday ? prev.streak + 1 : prev.lastLearnDate === today ? prev.streak : 1;
      const completedCourses = courseId && !prev.completedCourses.includes(courseId)
        ? [...prev.completedCourses, courseId] : prev.completedCourses;
      const newXP = prev.totalXP + 10;
      const badges = [...prev.badges];
      if (streak >= 7 && !badges.includes('week-streak')) badges.push('week-streak');
      if (streak >= 30 && !badges.includes('month-streak')) badges.push('month-streak');
      if (completedCourses.length >= 5 && !badges.includes('scholar')) badges.push('scholar');
      if (newXP >= 100 && !badges.includes('learner')) badges.push('learner');
      return { streak, lastLearnDate: today, badges, completedCourses, totalXP: newXP };
    });
  }, [setProgress]);

  return { ...progress, recordLesson };
}

// ── 18. Hide Footer (social media style) ─────────────────────
const HIDE_FOOTER_KEY = 'd88_hide_footer';

export function useHideFooter() {
  return usePersistedState<boolean>(HIDE_FOOTER_KEY, false);
}

// ── 19. Feature Request / Feedback ───────────────────────────
export function useFeedback() {
  const [submissions, setSubmissions] = usePersistedState<{ text: string; type: string; date: string }[]>('d88_feedback', []);
  const submit = useCallback((text: string, type: string) => {
    setSubmissions(prev => [{ text, type, date: new Date().toISOString() }, ...prev]);
  }, [setSubmissions]);
  return { submissions, submit };
}

export { usePersistedState };
